import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSetAtom } from "jotai";
import { activePopupState } from "../state/components/popup";
import LoadingState from "../components/basic/loading-state";
import { TablePagination } from "../components/basic/table";
import { resolveMediaUrl } from "../helpers/media-url";
import Mutations from "../params/mutations";
import Queries from "../params/queries";
import {
  accountsForExport,
  formatAccountsTxt,
} from "../../../shared/domain/account-export";
import {
  igHandleLabel,
  igProfileUrl,
  isLiveModelId,
  ProfileAvatar,
} from "./shared";
import type {
  AccountRow,
  ConfirmPopupRequest,
  FetchApi,
  FetchMultipart,
  RunMutation,
} from "../params/types";

const PAGE_SIZE = 10;

const Icon = ({ d, size = 18 }: { d: string; size?: number }) => (
  <svg
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
  >
    <path d={d} />
  </svg>
);

const EYE =
  "M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z";
const EYE_OFF =
  "M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21";
const GEAR =
  "M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.572-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.573-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z";

const CheckIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M4 12.6111L8.92308 17.5L20 6.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const WarningIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="17" r="1" fill="currentColor" />
    <path
      d="M12 10L12 14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.44722 18.1056L10.2111 4.57771C10.9482 3.10361 13.0518 3.10362 13.7889 4.57771L20.5528 18.1056C21.2177 19.4354 20.2507 21 18.7639 21H5.23607C3.7493 21 2.78231 19.4354 3.44722 18.1056Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function accountSearchBlob(account: AccountRow): string {
  return [
    account.username,
    account.ig_username,
    account.first_name,
    account.last_name,
    account.city,
    account.country,
    account.proxy_label,
    account.proxy_exit_ip,
    account.signup_email_string,
    account.signup_phone_string,
    account.model_id,
  ]
    .map((part) => String(part || "").toLowerCase())
    .join(" ");
}

/** Every word must appear in the login, name, email, phone, or proxy. */
function accountMatchesSearch(account: AccountRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const blob = accountSearchBlob(account);
  return needle.split(/\s+/).every((part) => {
    const token = part.replace(/^@/, "");
    return token ? blob.includes(token) : true;
  });
}

function proxyTypeHint(proxyLabel?: string | null) {
  const s = String(proxyLabel || "").toLowerCase();
  if (s.includes("socks")) return "socks";
  if (s.includes("http") || (proxyLabel && proxyLabel !== "none"))
    return "http";
  return null;
}

interface AccountsProps {
  accounts?: AccountRow[];
  processRunning?: boolean;
  busy?: boolean;
  loading?: boolean;
  fetchApi: FetchApi;
  fetchMultipart?: FetchMultipart | null;
  runMutation: RunMutation;
  flashSuccess: (message: string) => void;
  setError?: (message: string) => void;
  setBusy?: (busy: boolean) => void;
  setConfirmPopup?: (info: ConfirmPopupRequest) => void;
  onOpenMessages?: (username: string) => void;
  refresh?: (soft?: boolean) => void | Promise<unknown>;
  /** When on, Export leaves out suspended (banned) accounts. */
  hideSuspended?: boolean;
}

export default function Accounts({
  accounts = [],
  processRunning,
  busy,
  loading,
  fetchApi,
  fetchMultipart,
  runMutation,
  flashSuccess,
  setError,
  setConfirmPopup,
  onOpenMessages,
  refresh,
  hideSuspended = true,
}: AccountsProps) {
  const setAddPopup = useSetAtom(activePopupState("add_accounts"));
  const setSourcesPopup = useSetAtom(activePopupState("account_sources"));
  const setLogPopup = useSetAtom(activePopupState("account_log"));
  const setSettingsPopup = useSetAtom(activePopupState("account_settings"));
  const setProxyPopup = useSetAtom(activePopupState("set_account_proxy"));
  const [showPasswords, setShowPasswords] = useState(true);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchArmed, setSearchArmed] = useState(false);
  const [autofillGuard, setAutofillGuard] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const searchArmedRef = useRef(false);

  const filteredAccounts = useMemo(
    () => accounts.filter((account) => accountMatchesSearch(account, query)),
    [accounts, query],
  );
  const searching = query.trim().length > 0;
  const total = filteredAccounts.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const clampedPage = Math.min(page, Math.max(0, pageCount - 1));
  const pagedAccounts = filteredAccounts.slice(
    clampedPage * pageSize,
    clampedPage * pageSize + pageSize,
  );

  const setSearch = (value: string) => {
    setQuery(value);
    setPage(0);
  };

  const armSearch = () => {
    searchArmedRef.current = true;
    const el = searchRef.current;
    if (el) el.readOnly = false;
    setSearchArmed(true);
  };

  // Chrome writes the saved admin username into this field on load. A real
  // keystroke has an inputType; a password-manager fill does not.
  const rejectPasswordFill = (el: HTMLInputElement) => {
    const dirty = el.value !== "" || query !== "";
    el.value = "";
    if (!dirty) return;
    setQuery("");
    setPage(0);
    setAutofillGuard((n) => n + 1);
  };

  useLayoutEffect(() => {
    const el = searchRef.current;
    if (!el || searchArmed) return;
    if (el.value) el.value = "";
  }, [searchArmed, autofillGuard, query]);

  const confirmRemove = (username: string) => {
    const account = accounts.find((a) => a.username === username);
    if (account?.process_running) {
      setError?.("Stop this account before removing it");
      return;
    }
    setConfirmPopup?.({
      active: true,
      message: `Remove Instagram account "${username}" from Instabot?`,
      onConfirm: () => {
        runMutation(Mutations.RemoveAccount, { username }).then((res) => {
          if (res && !res.errors) flashSuccess(`Removed ${username}`);
        });
      },
    });
  };

  const confirmClearSession = (username: string) => {
    const account = accounts.find((a) => a.username === username);
    if (account?.process_running) {
      setError?.("Stop this account before logging out");
      return;
    }
    setConfirmPopup?.({
      active: true,
      message:
        `Log out of Instagram for "${username}"? ` +
        "This clears the saved browser session. Next start will need to sign in again.",
      onConfirm: () => {
        runMutation(Mutations.ClearSession, { username }).then((res) => {
          if (res && !res.errors) {
            flashSuccess(`Logged out ${username}`);
          }
        });
      },
    });
  };

  const exportAccounts = () => {
    const text = formatAccountsTxt(accountsForExport(accounts, hideSuspended));
    if (!text) {
      setError?.("No accounts to export");
      return;
    }
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "accounts.txt";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    const n = text.split("\n").filter(Boolean).length;
    flashSuccess(`Exported ${n} account${n === 1 ? "" : "s"}`);
  };

  const openAdd = async () => {
    let models: Array<{ id: string; label: string; city?: string | null }> =
      [];
    let modelsLoaded = false;
    try {
      const res = await fetchApi(Queries.Models);
      if (!res?.errors) {
        modelsLoaded = true;
        models = (res.models || [])
          .filter((model) => isLiveModelId(model.id))
          .map((model) => ({
            id: model.id,
            label: model.label || model.id,
            city: model.city,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
      }
    } catch {
      modelsLoaded = false;
    }
    setAddPopup({
      active: true,
      models,
      modelsLoaded,
      usedModelIds: accounts.map((account) => account.model_id),
      existingModels: accounts.map((account) => ({
        username: account.username,
        model_id: account.model_id,
      })),
      proxyOrdinalStart: accounts.length,
      onImport: async (input: {
        emails: string;
        proxies: string;
        model: string;
      }) => {
        const res = await runMutation(Mutations.ImportAccounts, { input });
        if (res && !res.errors && res.import_accounts) {
          const n = res.import_accounts.length;
          flashSuccess(`Added ${n} account${n === 1 ? "" : "s"}`);
        }
        return res;
      },
    });
  };

  const openLog = async (acc: AccountRow) => {
    setLogPopup({
      active: true,
      username: acc.username,
      logs: [],
      loading: true,
      fetchApi,
    });
    const res = await fetchApi(Queries.Logs, {
      account: acc.username,
      limit: 400,
    });
    setLogPopup({
      active: true,
      username: acc.username,
      logs: res?.logs || [],
      loading: false,
      fetchApi,
    });
  };

  const openSetProxy = () => {
    setProxyPopup({
      active: true,
      accounts,
      onApply: async (usernames: string[], proxy: string | null) => {
        if (!usernames.length) {
          return { ok: false, message: "Select at least one account" };
        }
        const res = await fetchApi(Mutations.UpsertAccounts, {
          inputs: usernames.map((username) => ({ username, proxy })),
        });
        if (res?.errors?.length) {
          const message = res.errors[0]?.message || "Could not set the proxy";
          setError?.(message);
          return { ok: false, message };
        }
        setError?.("");
        await refresh?.(true);
        const n = usernames.length;
        flashSuccess(
          proxy
            ? `Set proxy on ${n} account${n === 1 ? "" : "s"}`
            : `Cleared proxy on ${n} account${n === 1 ? "" : "s"}`,
        );
        return { ok: true };
      },
    });
  };

  const openSettings = (acc: AccountRow) => {
    setSettingsPopup({
      active: true,
      account: acc,
      fetchApi,
      fetchMultipart,
      runMutation,
      flashSuccess,
      setError,
      busy,
      processRunning,
      onSave: async (input: Record<string, unknown>) => {
        const res = await runMutation(Mutations.UpsertAccount, {
          input,
        });
        if (res && !res.errors) flashSuccess(`Updated ${acc.username}`);
      },
      onDelete: () => confirmRemove(acc.username),
      onClearSession: () => confirmClearSession(acc.username),
    });
  };

  return (
    <div className="ib-accounts-view">
      <div className="ib-accounts-toolbar">
        <div className="ib-accounts-toolbar-lead">
          <div className="ib-accounts-search">
            <label className="jf-visually-hidden" htmlFor="accounts-search">
              Search accounts
            </label>
            <input
              ref={searchRef}
              id="accounts-search"
              className="ib-accounts-search__input"
              type="search"
              role="searchbox"
              name="account-filter"
              value={query}
              placeholder="Search by handle, city, or proxy"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              readOnly={!searchArmed}
              data-1p-ignore="true"
              data-lpignore="true"
              data-bwignore="true"
              data-form-type="other"
              onPointerDown={armSearch}
              onFocus={armSearch}
              onAnimationStart={(e) => {
                if (e.animationName !== "ib-on-autofill") return;
                rejectPasswordFill(e.currentTarget);
              }}
              onChange={(e) => {
                const inputType = (e.nativeEvent as InputEvent).inputType || "";
                const typed =
                  inputType === "insertText" ||
                  inputType === "insertCompositionText" ||
                  inputType === "insertFromPaste" ||
                  inputType === "insertFromDrop" ||
                  inputType === "insertLineBreak" ||
                  inputType.startsWith("delete") ||
                  inputType === "historyUndo" ||
                  inputType === "historyRedo";
                if (!searchArmedRef.current || !typed) {
                  rejectPasswordFill(e.currentTarget);
                  return;
                }
                setSearch(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") setSearch("");
              }}
            />
            {query ? (
              <button
                type="button"
                className="ib-accounts-search__clear"
                aria-label="Clear search"
                onClick={() => setSearch("")}
              >
                ×
              </button>
            ) : null}
          </div>
          <div className="ib-accounts-count">
            {searching
              ? `${filteredAccounts.length} of ${accounts.length}`
              : accounts.length}{" "}
            account{accounts.length !== 1 ? "s" : ""}
          </div>
        </div>
        <div className="ib-accounts-toolbar-actions">
          <button
            type="button"
            className="jf-btn jf-btn-primary-light jf-btn-sm"
            onClick={() =>
              setSourcesPopup({
                active: true,
                fetchApi,
                fetchMultipart,
                flashSuccess,
                setError,
              })
            }
            disabled={busy}
          >
            Proxies & emails
          </button>
          <button
            type="button"
            className="jf-btn jf-btn-primary-light jf-btn-sm"
            onClick={openSetProxy}
            disabled={busy || !accounts.length}
            title="Set one proxy on selected accounts"
          >
            Set proxy
          </button>
          <button
            type="button"
            className="jf-btn jf-btn-primary-light jf-btn-sm"
            onClick={exportAccounts}
            disabled={!accounts.length}
            title="Download accounts as a .txt file"
          >
            Export
          </button>
          <button
            type="button"
            className="jf-btn jf-btn-primary-light jf-btn-sm"
            onClick={openAdd}
            disabled={busy}
          >
            + Add Accounts
          </button>
        </div>
      </div>

      {accounts.some((a) => a.process_running) && (
        <div className="ib-hint">
          Stop an account&apos;s process before removing it or clearing its
          session.
        </div>
      )}

      <div className="ib-accounts-table">
        <div className="ib-accounts-table__header">
          <span />
          <span>Username</span>
          <span className="ib-accounts-table__password-label">
            Password
            <button
              type="button"
              className="ib-accounts-table__eye-btn"
              title={showPasswords ? "Hide passwords" : "Show passwords"}
              aria-label={showPasswords ? "Hide passwords" : "Show passwords"}
              aria-pressed={showPasswords}
              onClick={() => setShowPasswords((shown) => !shown)}
            >
              <Icon d={showPasswords ? EYE_OFF : EYE} size={16} />
            </button>
          </span>
          <span>First Name</span>
          <span>Last Name</span>
          <span>Proxy</span>
          <span style={{ textAlign: "center" }}>Log</span>
          <span style={{ textAlign: "center" }}>Actions</span>
        </div>

        {loading && !accounts.length && (
          <div className="ib-accounts-table__empty">
            <LoadingState label="Loading accounts…" />
          </div>
        )}

        {!loading && accounts.length === 0 && (
          <div className="ib-accounts-table__empty">
            <div className="ib-accounts-table__empty-title">
              No accounts yet
            </div>
            <div className="ib-accounts-table__empty-text">
              Click &quot;Add Accounts&quot; to add your first accounts.
            </div>
          </div>
        )}

        {!loading && accounts.length > 0 && filteredAccounts.length === 0 && (
          <div className="ib-accounts-table__empty">
            <div className="ib-accounts-table__empty-title">
              No matching accounts
            </div>
            <div className="ib-accounts-table__empty-text">
              Nothing matches &quot;{query.trim()}&quot;.
            </div>
          </div>
        )}

        {pagedAccounts.map((a) => {
          const handle = igHandleLabel(a);
          const profileUrl = igProfileUrl(a);
          const pType = proxyTypeHint(a.proxy_label);
          const password = a.password || "";
          const hasIssue =
            Boolean(a.last_error) ||
            String(a.session_status || "").toLowerCase() === "banned" ||
            String(a.session_status || "").toLowerCase() === "challenge" ||
            (a.failed_outbound_count || 0) > 0;
          return (
            <div key={a.username} className="ib-accounts-table__row">
              <div className="ib-accounts-table__cell ib-accounts-table__cell--avatar">
                <ProfileAvatar
                  url={profileUrl}
                  text={a.ig_username || a.username}
                  image={resolveMediaUrl(a.avatar_url) || null}
                />
              </div>
              <div className="ib-accounts-table__cell ib-accounts-table__cell--user">
                <button
                  type="button"
                  className="ib-accounts-table__username-btn"
                  onClick={() => onOpenMessages?.(a.username)}
                  title="Open messages"
                >
                  {handle || a.username}
                </button>
                {handle ? (
                  <span className="ib-muted ib-accounts-table__login">
                    {a.username}
                  </span>
                ) : null}
                {handle &&
                  (profileUrl ? (
                    <a
                      className="ib-accounts-table__ig"
                      href={profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={profileUrl}
                    >
                      {profileUrl}
                    </a>
                  ) : null)}
                {a.has_password === false && (
                  <span className="ib-pill ib-pill--warn">no password</span>
                )}
              </div>
              <div className="ib-accounts-table__cell ib-accounts-table__cell--password">
                <span>{showPasswords && password ? password : "••••••••"}</span>
              </div>
              <div className="ib-accounts-table__cell ib-accounts-table__cell--ellipsis">
                <span>{a.first_name || "—"}</span>
              </div>
              <div className="ib-accounts-table__cell ib-accounts-table__cell--ellipsis">
                <span>{a.last_name || "—"}</span>
              </div>
              <div className="ib-accounts-table__cell ib-accounts-table__cell--proxy">
                {a.has_proxy ? (
                  <>
                    <span className="ib-mono">{a.proxy_label}</span>
                    {pType && <span className="ib-proxy-badge">{pType}</span>}
                    {a.proxy_exit_ip ? (
                      <span className="ib-proxy-exit">{a.proxy_exit_ip}</span>
                    ) : null}
                  </>
                ) : (
                  "—"
                )}
              </div>
              <div className="ib-accounts-table__cell ib-accounts-table__cell--center">
                {hasIssue ? (
                  <button
                    type="button"
                    className="ib-accounts-table__log-btn"
                    title="View log"
                    onClick={() => openLog(a)}
                  >
                    <WarningIcon size={20} />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="ib-accounts-table__log-ok"
                    title="View log"
                    onClick={() => openLog(a)}
                  >
                    <CheckIcon size={20} />
                  </button>
                )}
              </div>
              <div className="ib-accounts-table__cell ib-accounts-table__cell--actions">
                <button
                  type="button"
                  className="ib-accounts-table__gear-btn"
                  title="Settings"
                  onClick={() => openSettings(a)}
                >
                  <Icon d={GEAR} size={18} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {total > 0 && (
        <TablePagination
          page={clampedPage}
          pageCount={pageCount}
          total={total}
          pageSize={pageSize}
          onGotoPage={setPage}
          onPreviousPage={() => setPage((p) => Math.max(0, p - 1))}
          onNextPage={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          onSetPageSize={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      )}
    </div>
  );
}
